import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Login from './components/Login';
import Header from './components/Header';
import Home from './components/Home';
import Dashboard from './components/Dashboard';
import Detail from './components/Detail';
import Register from './components/Register';
import BulkUpload from './components/BulkUpload';
import GrokAI from './components/GrokAI';
import AuditLog from './components/AuditLog';
import Admin from './components/Admin';
import WebScraping from './components/WebScraping';
import Modulos from './components/Modulos';
import ModuloMonitoreoMedios from './components/ModuloMonitoreoMedios';
import ModuloBanderasRojas from './components/ModuloBanderasRojas';
import ModuloIAP from './components/ModuloIAP';
import ModuloDenuncias from './components/ModuloDenuncias';
import ModuloSeguimiento from './components/ModuloSeguimiento';
import VistaGeneralGestiones from './components/VistaGeneralGestiones';
import DetalleGestion from './components/DetalleGestion';
import TableroGeneral from './components/TableroGeneral';
import TableroCalificaciones from './components/TableroCalificaciones';
import TableroRendimiento from './components/TableroRendimiento';
import ConsultaCalificaciones from './components/ConsultaCalificaciones';
import ConsultaAsignaciones from './components/ConsultaAsignaciones';
import ConsultaInstanciasProbidad from './components/ConsultaInstanciasProbidad';
import SeguimientoCasosInstitucion from './components/SeguimientoCasosInstitucion';
import PanelCargaTrabajo from './components/PanelCargaTrabajo';
import TendenciaMonitoreo from './components/TendenciaMonitoreo';
import ControlAsignaciones from './components/ControlAsignaciones';
import '@/App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AppContent() {
  const { isAuthenticated, loading } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [currentView, setCurrentView] = useState('home');
  const [editingPost, setEditingPost] = useState(null);
  const [selectedGestion, setSelectedGestion] = useState(null);

  // Cargar datos de la API
  const fetchPosts = async () => {
    setLoadingPosts(true);
    try {
      const response = await axios.get(`${API}/posts`);
      setPosts(response.data || []);
    } catch (error) {
      console.error('Error al cargar publicaciones:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoadingPosts(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchPosts();
    }
  }, [isAuthenticated]);

  const handleViewChange = (view) => {
    setCurrentView(view);
    if (view === 'register') {
      setEditingPost(null);
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setCurrentView('register');
  };

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="loader"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <div className="app-container">
      <Header currentView={currentView} onViewChange={handleViewChange} />
      
      <main className="main-content">
        {loadingPosts && currentView !== 'home' && (
          <div className="loading-overlay">
            <div className="loader"></div>
          </div>
        )}

        {currentView === 'home' && (
          <Home onEnter={() => handleViewChange('dashboard')} onViewChange={handleViewChange} />
        )}

        {currentView === 'dashboard' && (
          <TableroGeneral />
        )}

        {currentView === 'detail' && (
          <Detail 
            posts={posts} 
            fetchPosts={fetchPosts} 
            onEdit={handleEdit}
          />
        )}

        {currentView === 'register' && (
          <Register 
            editingPost={editingPost}
            fetchPosts={fetchPosts}
            onSuccess={() => {
              handleViewChange('detail');
              setEditingPost(null);
            }}
            onCancel={() => setEditingPost(null)}
          />
        )}

        {currentView === 'bulk' && (
          <BulkUpload fetchPosts={fetchPosts} />
        )}

        {currentView === 'grok' && (
          <GrokAI />
        )}

        {currentView === 'audit' && (
          <AuditLog />
        )}

        {currentView === 'admin' && (
          <Admin />
        )}

        {currentView === 'scraping' && (
          <WebScraping />
        )}

        {currentView === 'modulos' && (
          <Modulos onModuleSelect={(modulo) => handleViewChange(modulo)} />
        )}

        {currentView === 'gestiones' && !selectedGestion && (
          <VistaGeneralGestiones 
            onViewDetail={(gestion) => setSelectedGestion(gestion.id)}
          />
        )}

        {currentView === 'gestiones' && selectedGestion && (
          <DetalleGestion
            gestionId={selectedGestion}
            onBack={() => setSelectedGestion(null)}
          />
        )}

        {currentView === 'reportes' && (
          <Dashboard posts={posts} fetchPosts={fetchPosts} />
        )}

        {currentView === 'monitoreo-medios' && (
          <ModuloMonitoreoMedios onBack={() => handleViewChange('modulos')} />
        )}

        {currentView === 'banderas-rojas' && (
          <ModuloBanderasRojas onBack={() => handleViewChange('modulos')} />
        )}

        {currentView === 'requerimientos-probidad' && (
          <ModuloIAP onBack={() => handleViewChange('modulos')} />
        )}

        {currentView === 'gestion-informacion' && (
          <ModuloDenuncias onBack={() => handleViewChange('modulos')} />
        )}

        {currentView === 'seguimiento' && (
          <ModuloSeguimiento onBack={() => handleViewChange('modulos')} />
        )}

        {currentView === 'tablero-calificaciones' && (
          <TableroCalificaciones onBack={() => handleViewChange('admin')} />
        )}

        {currentView === 'tablero-rendimiento' && (
          <TableroRendimiento />
        )}

        {currentView === 'consulta-calificaciones' && (
          <ConsultaCalificaciones onViewChange={handleViewChange} />
        )}

        {currentView === 'consulta-asignaciones' && (
          <ConsultaAsignaciones />
        )}

        {currentView === 'consulta-probidad' && (
          <ConsultaInstanciasProbidad />
        )}

        {currentView === 'seguimiento-instituciones' && (
          <SeguimientoCasosInstitucion />
        )}

        {currentView === 'panel-carga' && (
          <PanelCargaTrabajo />
        )}

        {currentView === 'tendencia-monitoreo' && (
          <TendenciaMonitoreo />
        )}

        {currentView === 'control-asignaciones' && (
          <ControlAsignaciones />
        )}
      </main>

      <footer className="footer">
        <div className="container">
          <p>ALERTA 360 — Sistema Integral de Gestión de Casos — {new Date().getFullYear()}</p>
        </div>
      </footer>

      <Toaster position="top-right" richColors />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
